/**
 * Agent Hub — Claude.ai-style web UI với:
 *  - SSE streaming (Phase 3.2)
 *  - Rate limit display (Phase 3.4)
 *  - Conversation threads (Phase 3.5): generate conversation_id, group history
 */
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    ListSubheader,
    Drawer,
    Chip,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Divider,
    Button,
    Tabs,
    Tab,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack
} from '@mui/material';
import {
    IconRobot,
    IconSend,
    IconPlayerStop,
    IconChevronDown,
    IconTool,
    IconCircleCheck,
    IconAlertCircle,
    IconLayoutSidebar,
    IconRefresh,
    IconHistory,
    IconUsersGroup,
    IconMessage,
    IconPlus,
    IconClipboard,
    IconX,
    IconEye,
    IconPhoto,
    IconUpload,
    IconHourglass,
    IconExternalLink
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api/v1' });
api.interceptors.request.use((cfg) => {
    const token = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

const ORCHESTRATOR = 'datahub-orchestrator';
const MAX_WIDTH = 760;
const CONVERSATION_KEY = 'agentHub.currentConversationId';

// Paste shortener thresholds — trên ngưỡng nào → giấu vào chip
const PASTE_THRESHOLD_LINES = 5;
const PASTE_THRESHOLD_CHARS = 250;

// Conversation length limits — giảm lag khi message nhiều
const DEFAULT_VISIBLE_COUNT = 5;     // Hiển thị 5 message gần nhất — snappy, không lag
const VISIBLE_INCREMENT = 5;         // Lazy load thêm 5 khi scroll up

// State persistence — chống mất tiến trình khi refresh
const STATE_KEY_PREFIX = 'agentHub.state.';
const MAX_PERSISTED_MESSAGES = 50;

const getAuthHeader = () => {
    const t = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    return t ? `Bearer ${t}` : '';
};

const newConvId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    // fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

const relTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'vừa xong';
    if (min < 60) return `${min} phút trước`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} giờ trước`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} ngày trước`;
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ============ Markdown ============
// Shared word-wrap rules — fix nội dung dài tràn phải (URL, tool output, long token).
const WRAP_SX = {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
};

const Md = ({ children }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            code({ inline, className, children, ...props }) {
                const m = /language-(\w+)/.exec(className || '');
                return !inline && m ? (
                    <Box sx={{ ...WRAP_SX, overflowX: 'auto', maxWidth: '100%' }}>
                        <SyntaxHighlighter
                            style={vscDarkPlus} language={m[1]} PreTag="div"
                            wrapLongLines
                            customStyle={{ borderRadius: 8, fontSize: 13, margin: '12px 0', maxWidth: '100%' }}
                            codeTagProps={{ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }}
                            {...props}
                        >
                            {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                    </Box>
                ) : (
                    <code style={{ background: '#f1f3f5', padding: '2px 6px', borderRadius: 4, fontSize: '0.92em', fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace', wordBreak: 'break-all', overflowWrap: 'anywhere' }} className={className} {...props}>{children}</code>
                );
            },
            p: ({ children }) => <Typography component="p" sx={{ my: 1, lineHeight: 1.7, fontSize: 15, ...WRAP_SX }}>{children}</Typography>,
            h1: ({ children }) => <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600, ...WRAP_SX }}>{children}</Typography>,
            h2: ({ children }) => <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600, ...WRAP_SX }}>{children}</Typography>,
            h3: ({ children }) => <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.5, fontWeight: 600, ...WRAP_SX }}>{children}</Typography>,
            ul: ({ children }) => <Box component="ul" sx={{ pl: 3, my: 1, ...WRAP_SX }}>{children}</Box>,
            ol: ({ children }) => <Box component="ol" sx={{ pl: 3, my: 1, ...WRAP_SX }}>{children}</Box>,
            li: ({ children }) => <Box component="li" sx={{ my: 0.3, lineHeight: 1.7, ...WRAP_SX }}>{children}</Box>,
            a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{children}</a>,
            table: ({ children }) => <Box sx={{ overflowX: 'auto', my: 1.5, maxWidth: '100%' }}><table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>{children}</table></Box>,
            th: ({ children }) => <th style={{ border: '1px solid #e1e4e8', padding: '8px 12px', background: '#f6f8fa', textAlign: 'left', wordBreak: 'break-word' }}>{children}</th>,
            td: ({ children }) => <td style={{ border: '1px solid #e1e4e8', padding: '8px 12px', wordBreak: 'break-word' }}>{children}</td>,
            pre: ({ children }) => <Box component="pre" sx={{ ...WRAP_SX, whiteSpace: 'pre-wrap', maxWidth: '100%', overflowX: 'auto', my: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, fontSize: 13 }}>{children}</Box>,
        }}
    >{children || ''}</ReactMarkdown>
);

// ============ Tool trace (compact grouped, default collapsed) ============
const _shortTool = (name) => (name || '').replace(/^mcp__\w+__/, '');

const _toolPreview = (input) => {
    if (!input) return '';
    if (input.method && input.path) return `${input.method} ${input.path}`;
    if (input.subagent_type) return `→ ${input.subagent_type}`;
    if (input.description) return String(input.description).slice(0, 60);
    if (input.command) return String(input.command).slice(0, 60);
    if (input.pattern) return `pattern: ${String(input.pattern).slice(0, 50)}`;
    if (input.file_path) {
        const fp = String(input.file_path);
        return fp.length > 50 ? '…' + fp.slice(-50) : fp;
    }
    if (input.query) return `query: ${String(input.query).slice(0, 50)}`;
    return '';
};

const ToolRow = ({ t }) => {
    const [expanded, setExpanded] = useState(false);
    const isPending = t.output_preview === '(pending)';
    return (
        <Box sx={{ borderTop: '1px solid', borderColor: 'grey.100', '&:first-of-type': { borderTop: 'none' } }}>
            <Box
                onClick={() => !isPending && setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: isPending ? 'default' : 'pointer',
                    '&:hover': isPending ? {} : { bgcolor: 'grey.50' }
                }}
            >
                {isPending ? <CircularProgress size={11} thickness={5} />
                 : t.success ? <IconCircleCheck size={13} color="#2e7d32" />
                 : <IconAlertCircle size={13} color="#d32f2f" />}
                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12, color: isPending ? 'text.secondary' : 'text.primary' }}>
                    {_shortTool(t.tool)}
                </Box>
                <Box component="span" sx={{ flex: 1, color: 'text.secondary', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {_toolPreview(t.input)}
                </Box>
                {t.latency_ms > 0 && (
                    <Box component="span" sx={{ color: 'text.secondary', fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {t.latency_ms < 1000 ? `${t.latency_ms}ms` : `${(t.latency_ms / 1000).toFixed(1)}s`}
                    </Box>
                )}
                {!isPending && (
                    <IconChevronDown size={11} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', opacity: 0.5 }} />
                )}
            </Box>
            {expanded && !isPending && (
                <Box sx={{ px: 1.5, py: 1, bgcolor: '#fafbfc' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10.5 }}>Input</Typography>
                    <Box component="pre" sx={{ m: 0, mt: 0.3, p: 0.75, bgcolor: '#fff', border: '1px solid', borderColor: 'grey.200', borderRadius: 0.5, fontSize: 11, overflow: 'auto', maxHeight: 120, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {JSON.stringify(t.input, null, 2)}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10.5, display: 'block', mt: 0.5 }}>Output</Typography>
                    <Box component="pre" sx={{ m: 0, mt: 0.3, p: 0.75, bgcolor: '#fff', border: '1px solid', borderColor: 'grey.200', borderRadius: 0.5, fontSize: 11, overflow: 'auto', maxHeight: 180, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {t.output_preview}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const ToolTrace = ({ trace }) => {
    const [open, setOpen] = useState(false);
    if (!trace?.length) return null;

    // Stats
    const success = trace.filter((t) => t.success && t.output_preview !== '(pending)').length;
    const failed = trace.filter((t) => !t.success && t.output_preview !== '(pending)').length;
    const pending = trace.filter((t) => t.output_preview === '(pending)').length;
    const totalLatency = trace.reduce((s, t) => s + (t.latency_ms || 0), 0);

    // Group by tool name → "Agent×2, Bash×3, call_internal_api×5, Read×2"
    const groups = {};
    trace.forEach((t) => {
        const k = _shortTool(t.tool);
        groups[k] = (groups[k] || 0) + 1;
    });
    const groupEntries = Object.entries(groups).sort((a, b) => b[1] - a[1]);

    const headerColor = failed > 0 ? 'error.light' : pending > 0 ? 'grey.300' : 'success.light';
    const headerBg = failed > 0 ? 'error.50' : pending > 0 ? 'grey.50' : 'success.50';

    return (
        <Box sx={{
            my: 1.5,
            border: '1px solid',
            borderColor: headerColor,
            bgcolor: headerBg,
            borderRadius: 1,
            overflow: 'hidden'
        }}>
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' }
                }}
            >
                <IconTool size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 12 }}>
                    {trace.length} tool call{trace.length > 1 ? 's' : ''}
                </Typography>
                {pending > 0 && <CircularProgress size={11} thickness={5} />}
                {success > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#2e7d32' }}>
                        <IconCircleCheck size={12} />
                        <Typography variant="caption" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{success}</Typography>
                    </Box>
                )}
                {failed > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#d32f2f' }}>
                        <IconAlertCircle size={12} />
                        <Typography variant="caption" sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{failed}</Typography>
                    </Box>
                )}
                <Box sx={{ flex: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', minWidth: 0, overflow: 'hidden' }}>
                    {groupEntries.slice(0, 6).map(([k, n]) => (
                        <Box key={k} component="span" sx={{ fontSize: 10.5, fontFamily: 'monospace', color: 'text.secondary', bgcolor: '#fff', border: '1px solid', borderColor: 'grey.300', borderRadius: 0.5, px: 0.5, py: 0.1, whiteSpace: 'nowrap' }}>
                            {k}{n > 1 ? `×${n}` : ''}
                        </Box>
                    ))}
                    {groupEntries.length > 6 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>+{groupEntries.length - 6}</Typography>
                    )}
                </Box>
                {totalLatency > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, fontVariantNumeric: 'tabular-nums' }}>
                        {totalLatency < 1000 ? `${totalLatency}ms` : `${(totalLatency / 1000).toFixed(1)}s`}
                    </Typography>
                )}
                <IconChevronDown size={13} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', opacity: 0.6 }} />
            </Box>
            {open && (
                <Box sx={{ bgcolor: '#fff' }}>
                    {trace.map((t, i) => <ToolRow key={i} t={t} />)}
                </Box>
            )}
        </Box>
    );
};

// ============ UserQuestionCard ============
// Render khi agent gọi tool ask_user_question. User trả lời → POST /turns/{id}/answer
// payload: { question_id, question, options: [{label, description}], multiSelect, header }
const UserQuestionCard = ({ question, onSubmit, onCancel }) => {
    const [selected, setSelected] = useState(question.multiSelect ? [] : null);
    const [otherText, setOtherText] = useState('');
    const [useOther, setUseOther] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const canSubmit = useOther
        ? otherText.trim().length > 0
        : question.multiSelect
            ? (selected || []).length > 0
            : selected !== null && selected !== undefined;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        const answer = useOther
            ? otherText.trim()
            : question.multiSelect
                ? selected
                : selected;
        try {
            await onSubmit(answer, useOther ? '(user entered Other)' : null);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{
            mt: 2, mb: 2, mx: 'auto', maxWidth: 720,
            border: '2px solid', borderColor: 'warning.main', borderRadius: 2,
            bgcolor: '#fffbeb', p: 2.5,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 1, bgcolor: 'warning.main', color: 'white' }}>
                    <IconRobot size={16} />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: 'warning.dark' }}>
                    Agent cần input của bạn
                </Typography>
                {question.header && (
                    <Chip label={question.header} size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />
                )}
            </Box>
            <Typography sx={{ fontSize: 14.5, mb: 2, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                {question.question}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                {(question.options || []).map((opt, idx) => {
                    const isSelected = question.multiSelect
                        ? (selected || []).includes(opt.label)
                        : selected === opt.label;
                    return (
                        <Box
                            key={idx}
                            onClick={() => {
                                setUseOther(false);
                                if (question.multiSelect) {
                                    setSelected((cur) => {
                                        const arr = cur || [];
                                        return arr.includes(opt.label) ? arr.filter((x) => x !== opt.label) : [...arr, opt.label];
                                    });
                                } else {
                                    setSelected(opt.label);
                                }
                            }}
                            sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1.25,
                                p: 1.25, borderRadius: 1, cursor: 'pointer',
                                border: '1.5px solid',
                                borderColor: isSelected && !useOther ? 'warning.main' : 'grey.200',
                                bgcolor: isSelected && !useOther ? '#fef3c7' : 'white',
                                '&:hover': { bgcolor: isSelected && !useOther ? '#fef3c7' : 'grey.50' },
                            }}
                        >
                            <Box sx={{
                                mt: 0.25, flexShrink: 0, width: 16, height: 16, borderRadius: question.multiSelect ? 0.5 : '50%',
                                border: '2px solid', borderColor: isSelected && !useOther ? 'warning.main' : 'grey.400',
                                bgcolor: isSelected && !useOther ? 'warning.main' : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {isSelected && !useOther && (
                                    <Box sx={{ width: 8, height: 8, borderRadius: question.multiSelect ? 0 : '50%', bgcolor: 'white' }} />
                                )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: 'text.primary' }}>
                                    {opt.label}
                                </Typography>
                                {opt.description && (
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                                        {opt.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
                {/* Other option */}
                <Box
                    sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.25,
                        p: 1.25, borderRadius: 1,
                        border: '1.5px solid', borderColor: useOther ? 'warning.main' : 'grey.200',
                        bgcolor: useOther ? '#fef3c7' : 'white',
                    }}
                >
                    <Box
                        onClick={() => { setUseOther(true); setSelected(question.multiSelect ? [] : null); }}
                        sx={{
                            mt: 0.25, flexShrink: 0, width: 16, height: 16, borderRadius: '50%',
                            border: '2px solid', borderColor: useOther ? 'warning.main' : 'grey.400',
                            bgcolor: useOther ? 'warning.main' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        }}
                    >
                        {useOther && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'white' }} />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: 'text.primary' }}>Other</Typography>
                        <TextField
                            placeholder="Nhập đáp án của bạn..."
                            value={otherText}
                            onChange={(e) => { setOtherText(e.target.value); setUseOther(true); }}
                            onFocus={() => setUseOther(true)}
                            size="small"
                            fullWidth
                            sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { fontSize: 13, py: 0.25 } }}
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} disabled={submitting} size="small" sx={{ textTransform: 'none' }} color="inherit">
                    Huỷ turn
                </Button>
                <Button onClick={handleSubmit} disabled={!canSubmit || submitting} size="small" variant="contained" color="warning" sx={{ textTransform: 'none', minWidth: 100 }}>
                    {submitting ? <CircularProgress size={14} color="inherit" /> : 'Gửi đáp án'}
                </Button>
            </Box>
        </Box>
    );
};

// ============ WatcherCard ============
// Render mỗi event từ background watcher (value_changed | stopped | cancelled)
const WatcherCard = ({ event }) => {
    const isStopped = event.type === 'stopped' || event.type === 'cancelled';
    return (
        <Box sx={{
            my: 1.5, mx: 'auto', maxWidth: 720,
            borderLeft: '3px solid',
            borderColor: isStopped ? 'success.main' : 'info.main',
            bgcolor: isStopped ? '#f0fdf4' : '#eff6ff',
            borderRadius: 1, p: 1.25,
            display: 'flex', alignItems: 'flex-start', gap: 1,
        }}>
            <Box sx={{ pt: 0.25 }}>
                {isStopped ? <IconCircleCheck size={16} color="#16a34a" /> : <IconHourglass size={16} color="#2563eb" />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: isStopped ? 'success.dark' : 'info.dark' }}>
                        🔔 Watcher · {event.name}
                    </Typography>
                    <Chip size="small" label={event.type} sx={{ height: 16, fontSize: 10, bgcolor: 'white' }} />
                </Box>
                {event.type === 'value_changed' && (
                    <Box>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
                            {event.check_path}{event.extract_path ? ` → ${event.extract_path}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', fontSize: 12 }}>
                            {event.before !== null && (
                                <Box sx={{ bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 0.5, color: 'text.secondary' }}>
                                    Trước: <code>{String(event.before).slice(0, 80)}</code>
                                </Box>
                            )}
                            <Box sx={{ bgcolor: 'white', px: 0.75, py: 0.25, borderRadius: 0.5, fontWeight: 500 }}>
                                Sau: <code>{String(event.after || '(empty)').slice(0, 120)}</code>
                            </Box>
                        </Box>
                    </Box>
                )}
                {event.type === 'stopped' && (
                    <Typography sx={{ fontSize: 12, color: 'success.dark' }}>
                        Watcher hoàn tất — {event.reason}. Final: <code>{String(event.final_value || '').slice(0, 120)}</code>
                    </Typography>
                )}
                {event.type === 'cancelled' && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Đã huỷ.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const Welcome = ({ isOrchestrator, agentName, onSample }) => {
    const samples = isOrchestrator
        ? ['List các project hiện có của tôi', 'Check Celery queue depth', 'Tạo project mới tên Test-1234, quota 5GB', 'Cách upload dataset YOLO format?']
        : [`${agentName} có thể giúp gì?`, 'Giải thích kiến trúc module này', 'Best practices khi triển khai feature'];
    return (
        <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', textAlign: 'center', pt: 8 }}>
            <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: '50%', bgcolor: 'primary.main', mb: 2 }}><IconRobot size={32} color="white" /></Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, fontSize: 28 }}>{isOrchestrator ? 'DataHub Agent Hub' : agentName}</Typography>
            <Typography color="text.secondary" sx={{ mb: 4, fontSize: 15, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
                {isOrchestrator ? 'Tôi điều phối 19 sub-agent + backend API.' : 'Sub-agent chuyên môn — chat advisory.'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, maxWidth: 600, mx: 'auto' }}>
                {samples.map((s) => (
                    <Button key={s} variant="outlined" onClick={() => onSample(s)}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', color: 'text.primary', borderColor: 'grey.300', py: 1.2, px: 1.5, fontSize: 13.5, '&:hover': { bgcolor: 'grey.50' } }}>
                        {s}
                    </Button>
                ))}
            </Box>
        </Box>
    );
};

// ============ SSE parser ============
// Common SSE parser → callbacks. Detects `id:` line (Redis Stream entry ID) for resume tracking.
async function parseSSEStream(res, callbacks) {
    const { onText, onToolUse, onToolResult, onDone, onError, onStart, onTurnOpen, onRateLimit, onEventId, onUserQuestion, onUserQuestionTimeout, onServerRestart } = callbacks;
    if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Rate limit. Reset in ${data?.detail?.reset_in_seconds || '?'}s`);
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    if (!res.body) throw new Error('No body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        while (true) {
            const idx = buf.indexOf('\n\n');
            if (idx < 0) break;
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            let event = 'message';
            let data = null;
            let entryId = null;
            for (const line of block.split('\n')) {
                if (line.startsWith('event:')) event = line.slice(6).trim();
                else if (line.startsWith('id:')) entryId = line.slice(3).trim();
                else if (line.startsWith('data:')) {
                    try { data = JSON.parse(line.slice(5).trim()); } catch { data = line.slice(5).trim(); }
                }
            }
            if (data == null) continue;
            if (entryId && onEventId) onEventId(entryId);
            if (event === 'turn_open' && onTurnOpen) onTurnOpen(data);
            else if (event === 'rate_limit' && onRateLimit) onRateLimit(data);
            else if (event === 'start' && onStart) onStart(data);
            else if (event === 'text' && onText) onText(data.delta || '');
            else if (event === 'tool_use' && onToolUse) onToolUse(data);
            else if (event === 'tool_result' && onToolResult) onToolResult(data);
            else if (event === 'user_question' && onUserQuestion) onUserQuestion(data);
            else if (event === 'user_question_timeout' && onUserQuestionTimeout) onUserQuestionTimeout(data);
            else if (event === 'done' && onDone) onDone(data);
            else if (event === 'server_restart' && onServerRestart) onServerRestart(data);
            else if (event === 'error' && onError) onError(data);
        }
    }
}

async function streamChat({ agent_name, message, conversation_id, signal, ...callbacks }) {
    const baseUrl = (process.env.REACT_APP_API_URL || '/api/v1');
    const res = await fetch(`${baseUrl}/agent-hub/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
        body: JSON.stringify({ agent_name, message, conversation_id }),
        signal,
    });
    return parseSSEStream(res, callbacks);
}

// Phase 1.5: resume 1 turn đang chạy (sau khi user refresh trang)
async function resumeTurnStream({ turn_id, from_id = '0-0', signal, ...callbacks }) {
    const baseUrl = (process.env.REACT_APP_API_URL || '/api/v1');
    const url = `${baseUrl}/agent-hub/turns/${turn_id}/stream?from_id=${encodeURIComponent(from_id)}`;
    const res = await fetch(url, {
        headers: { Authorization: getAuthHeader() },
        signal,
    });
    return parseSSEStream(res, callbacks);
}

// ============ Main ============
// ChatTab — 1 instance per in-app tab. State isolated, SSE chạy độc lập per instance.
// Props:
//   - tabId: string ID của tab (để shell quản lý)
//   - initialConversationId: convId muốn load khi mount (null = chat mới)
//   - agents: list agents (cache ở shell, không fetch lại cho mỗi tab)
//   - isActive: tab có đang hiển thị không (dùng để decide focus + URL sync)
//   - onConvChange(tabId, convId, label): callback khi conv thay đổi (shell update tab list)
//   - onStatusChange(tabId, status): { streaming, pendingCount } để shell render badge tab
//   - onOpenNewTab(initialConvId?): mở tab mới (shell handle)
function ChatTab({ tabId, initialConversationId, agents, isActive, onConvChange, onStatusChange, onOpenNewTab }) {
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState(0);
    const [conversations, setConversations] = useState([]);
    // ConversationId của tab này — init từ prop của shell (mỗi tab có conv riêng).
    const [conversationId, setConversationId] = useState(initialConversationId || null);
    const [rateLimit, setRateLimit] = useState(null);
    const [streamingMsg, setStreamingMsg] = useState(null);
    // /btw parallel sub-turn (chạy song song với main turn, không cancel current)
    const [btwStreamingMsg, setBtwStreamingMsg] = useState(null);
    const [btwLoading, setBtwLoading] = useState(false);
    const [pastes, setPastes] = useState([]); // Pasted long-text attachments
    const [previewPaste, setPreviewPaste] = useState(null); // For modal preview
    const [images, setImages] = useState([]); // Pasted/uploaded images
    const [uploadingImage, setUploadingImage] = useState(false);
    // Queue (legacy — chỉ dùng cho restore từ localStorage cũ)
    const [pendingQueue, setPendingQueue] = useState([]);
    const [pendingExpanded, setPendingExpanded] = useState(false); // collapsed bar mặc định, click để xem detail
    // Interactive question từ agent (ask_user_question tool) — render UI card cho user trả lời
    const [pendingQuestion, setPendingQuestion] = useState(null); // { question_id, question, options, multiSelect, header }
    // Phase 2: watcher events stream + active watchers list
    const [watcherEvents, setWatcherEvents] = useState([]); // chronological list of watcher events
    const [watchers, setWatchers] = useState([]); // active watchers metadata
    const watcherESRef = useRef(null);
    // Server restart UX (2026-05-22): lưu last user message để có nút "Gửi lại" sau restart
    const [serverRestart, setServerRestart] = useState(null); // {message, partial_reply, last_user_text, last_pastes, last_images}
    const lastUserMessageRef = useRef(null); // {text, pastes, images} của message vừa gửi
    const sendMessageRef = useRef(null); // ref tới sendMessage để useEffect dùng (tránh stale closure)
    // Phase 1.5: track turn đang chạy để resume sau refresh
    const activeTurnRef = useRef(null); // { turn_id, lastEventId }
    const btwActiveTurnRef = useRef(null); // turn /btw parallel
    // B4: abort controller cho fetch stream — cleanup khi tab unmount tránh setState
    // trên unmounted component + memory leak
    const streamAbortRef = useRef(null);
    const btwStreamAbortRef = useRef(null);
    // B5: dedupe resume — chỉ kết nối SSE 1 lần cho mỗi turn_id (tránh 2 tab cùng convId)
    const resumedTurnsRef = useRef(new Set());
    const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);
    // Lazy loading: sentinel ref ở đầu message list, IntersectionObserver trigger load more
    const lazyLoadSentinelRef = useRef(null);
    const isLoadingMoreRef = useRef(false); // debounce multiple triggers
    const [compacting, setCompacting] = useState(false);
    // Sau compact: carry-over summary làm context cho LẦN gửi message kế tiếp (1-shot)
    const [pendingContextPrefix, setPendingContextPrefix] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    // Agents từ prop (shell fetch 1 lần) → pick selectedAgent khi list sẵn sàng
    useEffect(() => {
        if (!agents || agents.length === 0) return;
        if (!selectedAgent) {
            const master = agents.find((a) => a.name === ORCHESTRATOR);
            setSelectedAgent(master || agents[0]);
        }
    }, [agents, selectedAgent]);

    // Auto-scroll bottom khi new message arrive
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 300;
        if (nearBottom) el.scrollTop = el.scrollHeight;
    }, [messages, streamingMsg, btwStreamingMsg, loading]);

    // Auto-shrink: khi user scroll về bottom + visibleCount đã expand → reset về 5.
    // Dùng onScroll handler (không phải useEffect) vì scroll event không trigger re-render.
    const handleScrollShrink = useCallback(() => {
        const el = scrollRef.current;
        if (!el || visibleCount <= DEFAULT_VISIBLE_COUNT) return;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
        if (nearBottom) {
            setVisibleCount(DEFAULT_VISIBLE_COUNT);
        }
    }, [visibleCount]);

    // Lazy loading: IntersectionObserver trên sentinel ở đầu message list.
    // Khi user scroll lên gần top → auto load thêm older messages.
    // Maintain scroll position: save scrollHeight trước, restore sau prepend.
    useEffect(() => {
        const sentinel = lazyLoadSentinelRef.current;
        const container = scrollRef.current;
        if (!sentinel || !container) return;
        const hasMore = messages.length > visibleCount;
        if (!hasMore) return; // nothing to lazy load

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isLoadingMoreRef.current) {
                    isLoadingMoreRef.current = true;
                    const prevHeight = container.scrollHeight;
                    setVisibleCount((c) => Math.min(c + VISIBLE_INCREMENT, messages.length));
                    // Restore scroll position sau DOM update (requestAnimationFrame)
                    requestAnimationFrame(() => {
                        const newHeight = container.scrollHeight;
                        container.scrollTop += (newHeight - prevHeight);
                        isLoadingMoreRef.current = false;
                    });
                }
            },
            { root: container, rootMargin: '200px 0px 0px 0px', threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [messages.length, visibleCount]);

    // Legacy queue: chỉ flush nếu pendingQueue có item từ localStorage cũ (backward compat).
    // /btw bây giờ fire parallel turn ngay, không enqueue nữa.
    useEffect(() => {
        if (!loading && pendingQueue.length > 0 && sendMessageRef.current) {
            const next = pendingQueue[0];
            setPendingQueue((q) => q.slice(1));
            setTimeout(() => sendMessageRef.current?.(next.text, {
                text: next.text,
                pastes: next.pastes,
                images: next.images
            }), 50);
        }
    }, [loading, pendingQueue]);

    // Phase 2: open EventSource cho watcher events khi conv có giá trị
    useEffect(() => {
        // Cleanup ES cũ trước khi mở mới
        if (watcherESRef.current) {
            try { watcherESRef.current.close(); } catch {}
            watcherESRef.current = null;
        }
        if (!conversationId) return;
        // Lấy danh sách watchers hiện có (cho panel)
        api.get(`/agent-hub/conversations/${conversationId}/watchers`).then((r) => {
            setWatchers(r.data || []);
        }).catch(() => {});

        // EventSource cần token qua URL? api uses cookies/jwt header — EventSource không hỗ trợ
        // custom headers. Workaround: dùng fetch streaming giống streamChat.
        const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
        const ctrl = new AbortController();
        watcherESRef.current = { close: () => ctrl.abort() };

        fetch(`${baseUrl}/agent-hub/conversations/${conversationId}/events/stream`, {
            headers: { Authorization: getAuthHeader() },
            signal: ctrl.signal,
        }).then(async (res) => {
            if (!res.ok || !res.body) return;
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                while (true) {
                    const idx = buf.indexOf('\n\n');
                    if (idx < 0) break;
                    const block = buf.slice(0, idx);
                    buf = buf.slice(idx + 2);
                    let ev = 'message';
                    let dt = null;
                    for (const line of block.split('\n')) {
                        if (line.startsWith('event:')) ev = line.slice(6).trim();
                        else if (line.startsWith('data:')) {
                            try { dt = JSON.parse(line.slice(5).trim()); } catch { dt = line.slice(5).trim(); }
                        }
                    }
                    if (ev === 'watcher' && dt) {
                        // P2-P5: cap array tại 100 events (UI chỉ render 20 cuối)
                        setWatcherEvents((prev) => {
                            const next = [...prev, { ...dt, _received_at: Date.now() }];
                            return next.length > 100 ? next.slice(-100) : next;
                        });
                        // Re-fetch list khi có 'stopped' hoặc 'cancelled' để cập nhật status
                        if (dt.type === 'stopped' || dt.type === 'cancelled') {
                            api.get(`/agent-hub/conversations/${conversationId}/watchers`)
                                .then((r) => setWatchers(r.data || [])).catch(() => {});
                        }
                    }
                }
            }
        }).catch((e) => {
            if (e?.name !== 'AbortError') console.warn('watcher ES failed:', e);
        });

        return () => {
            if (watcherESRef.current) {
                try { watcherESRef.current.close(); } catch {}
                watcherESRef.current = null;
            }
        };
    }, [conversationId]);

    // Báo shell mỗi khi conv của tab này thay đổi (shell sẽ update tab list + URL nếu là tab active)
    useEffect(() => {
        if (onConvChange) {
            const label = messages.find((m) => m.role === 'user')?.content?.slice(0, 40) || null;
            onConvChange(tabId, conversationId, label);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId, messages.length]);

    // Báo shell status để hiện badge tab (●=streaming, ⏳=pending)
    useEffect(() => {
        if (onStatusChange) {
            onStatusChange(tabId, {
                streaming: streamingMsg !== null || loading || btwStreamingMsg !== null || btwLoading,
                pendingCount: pendingQueue.length + (btwLoading ? 1 : 0),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [streamingMsg, btwStreamingMsg, loading, btwLoading, pendingQueue.length]);

    const loadConversations = useCallback(async () => {
        try {
            const res = await api.get('/agent-hub/conversations', { params: { limit: 50 } });
            setConversations(res.data || []);
        } catch (e) {
            console.warn('Cannot load conversations', e);
        }
    }, []);

    const newConversation = () => {
        setMessages([]);
        setStreamingMsg(null);
        setBtwStreamingMsg(null);
        setBtwLoading(false);
        setConversationId(null);
        setError(null);
        setVisibleCount(DEFAULT_VISIBLE_COUNT);
        setPendingContextPrefix(null);
        setPendingQueue([]);
    };

    // ── Slash commands (Claude CLI-style) ─────────────────────────────────
    const SLASH_COMMANDS = useMemo(() => [
        { name: '/btw', args: '<message>', desc: 'Gửi sub-message chạy SONG SONG với turn hiện tại (giống Claude CLI /btw). KHÔNG cancel turn cũ.' },
        { name: '/stop', args: '[btw]', desc: 'Dừng turn chính. /stop btw → dừng riêng sub-turn /btw.' },
        { name: '/compact', args: '', desc: 'Tóm tắt conversation cũ thành 1 summary (giảm context cost).' },
        { name: '/refresh-context', args: '', desc: 'Refresh project metadata cache (nếu files/collections thay đổi).' },
        { name: '/clear', args: '', desc: 'Hội thoại mới (giữ tab, reset messages).' },
        { name: '/help', args: '', desc: 'Hiển thị danh sách lệnh slash.' },
    ], []);

    const handleSlashCommand = async (cmdText, ctx) => {
        // cmdText: full input bắt đầu bằng '/'
        const [cmd, ...rest] = cmdText.split(/\s+/);
        const argText = rest.join(' ');
        if (cmd === '/btw') {
            const note = argText.trim();
            if (!note && !ctx.hasPastes && !ctx.hasImages) {
                setError('Cú pháp: /btw <message>');
                return true;
            }
            if (!selectedAgent) {
                setError('Chưa chọn agent.');
                return true;
            }
            if (btwLoading) {
                setError('Đã có sub-turn /btw đang chạy. Đợi xong hoặc /stop btw.');
                return true;
            }
            // Nếu không có turn chính đang chạy → /btw == message thường
            if (!loading) {
                setInput('');
                setPastes([]);
                setImages([]);
                sendMessageRef.current?.(note, { text: note, pastes: ctx.usePastes, images: ctx.useImages });
                return true;
            }
            // Fire parallel sub-turn
            setInput('');
            setPastes([]);
            setImages([]);
            sendBtwMessage(note, ctx.usePastes, ctx.useImages);
            return true;
        }
        if (cmd === '/stop') {
            const target = argText.trim().toLowerCase();
            if (target === 'btw') {
                const tid = btwActiveTurnRef.current?.turn_id;
                if (!tid) {
                    setError('Không có sub-turn /btw đang chạy.');
                    return true;
                }
                try { await api.post(`/agent-hub/turns/${tid}/stop`); }
                catch (e) { setError('Stop BTW turn thất bại: ' + (e?.message || 'unknown')); }
                setInput('');
                return true;
            }
            const tid = activeTurnRef.current?.turn_id;
            if (!tid) {
                setError('Không có turn đang chạy để dừng.');
                return true;
            }
            try { await api.post(`/agent-hub/turns/${tid}/stop`); }
            catch (e) { setError('Stop turn thất bại: ' + (e?.message || 'unknown')); }
            setInput('');
            return true;
        }
        if (cmd === '/compact') {
            setInput('');
            handleCompact();
            return true;
        }
        if (cmd === '/refresh-context') {
            setInput('');
            if (conversationId) {
                try {
                    await api.delete(`/agent-hub/conversations/${conversationId}/project-context`);
                    setMessages((m) => [...m, {
                        role: 'assistant', agent: 'system',
                        content: '✅ Project context cache cleared. Turn tiếp theo sẽ re-discover metadata.',
                        historical: true,
                    }]);
                } catch (e) {
                    setError(e?.message || 'Clear context failed');
                }
            }
            return true;
        }
        if (cmd === '/clear') {
            setInput('');
            newConversation();
            return true;
        }
        if (cmd === '/help') {
            const helpText = '### 📖 Lệnh slash khả dụng\n\n' +
                SLASH_COMMANDS.map((c) => `- \`${c.name}${c.args ? ' ' + c.args : ''}\` — ${c.desc}`).join('\n') +
                '\n\n*Mọi message KHÔNG bắt đầu bằng `/` đều là chat thường gửi tới agent.*';
            setMessages((m) => [...m, {
                role: 'assistant',
                agent: 'system',
                content: helpText,
                historical: true,
            }]);
            setInput('');
            return true;
        }
        // Unknown slash command
        if (cmdText.match(/^\/\w+/)) {
            setError(`Lệnh "${cmd}" không tồn tại. Gõ /help để xem danh sách.`);
            return true;
        }
        return false;
    };

    // Compact: gọi backend tóm tắt → replace old messages bằng 1 summary
    const handleCompact = async () => {
        if (!conversationId || compacting) return;
        if (!window.confirm(
            `Tóm tắt ${messages.length} messages thành 1 summary?\n\nĐiều này sẽ:\n- Giữ ${Math.min(5, messages.length)} message gần nhất nguyên\n- Replace phần trước bằng 1 summary tự động (gọi Claude)\n- Cost ~$0.02-0.05`
        )) return;
        setCompacting(true);
        try {
            const res = await api.post(`/agent-hub/conversations/${conversationId}/compact`, {
                keep_recent: 5
            });
            const summary = res.data?.summary || '(summary empty)';
            const kept = messages.slice(-(res.data?.keep_recent || 5));
            setMessages([
                {
                    role: 'assistant',
                    agent: 'system',
                    content: `### 📋 Conversation Summary (compacted)\n\n${summary}\n\n---\n\n*${res.data?.original_count || 0} messages cũ đã được tóm tắt. Bên dưới là ${kept.length} message gần nhất.*`,
                    historical: true
                },
                ...kept
            ]);
            setVisibleCount(DEFAULT_VISIBLE_COUNT);
            // Carry-over summary làm context cho lần chat kế tiếp (1-shot)
            // → Agent SẼ biết context dù SDK session đã clear
            setPendingContextPrefix(summary);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Compact failed');
        } finally {
            setCompacting(false);
        }
    };

    // Update ref mỗi render — useEffect dùng ref tránh stale closure
    useEffect(() => {
        sendMessageRef.current = sendMessage;
    });

    // ─── State persistence: save snapshot khi state đổi ─────────────────
    // Restore khi mount (cứu user khi refresh giữa stream)
    useEffect(() => {
        if (!conversationId) return;
        const key = `${STATE_KEY_PREFIX}${conversationId}`;
        const snapshot = {
            messages: messages.slice(-MAX_PERSISTED_MESSAGES),
            streamingMsg,
            pendingQueue: pendingQueue.map(({ images, ...rest }) => ({
                ...rest,
                // KHÔNG persist blob URLs (mất sau reload)
                images: (images || []).map(({ previewUrl, ...meta }) => meta)
            })),
            pendingContextPrefix,
            input,
            ts: Date.now()
        };
        try {
            localStorage.setItem(key, JSON.stringify(snapshot));
        } catch (e) {
            // P2-P6: QuotaExceeded → retry với ít message hơn để không mất hoàn toàn
            try {
                const shrunk = { ...snapshot, messages: snapshot.messages.slice(-20) };
                localStorage.setItem(key, JSON.stringify(shrunk));
                console.warn('localStorage quota exceeded, persisted only last 20 messages');
            } catch {}
        }
    }, [messages, streamingMsg, pendingQueue, pendingContextPrefix, input, conversationId]);

    // Restore từ localStorage khi conversation_id đầu tiên set
    const restoredRef = useRef(new Set());
    useEffect(() => {
        if (!conversationId || restoredRef.current.has(conversationId)) return;
        restoredRef.current.add(conversationId);
        const key = `${STATE_KEY_PREFIX}${conversationId}`;
        try {
            const saved = localStorage.getItem(key);
            if (!saved) return;
            const data = JSON.parse(saved);
            // Chỉ restore nếu state hiện tại "rỗng" (mới reload, chưa load gì)
            if (messages.length === 0 && data.messages?.length) {
                setMessages(data.messages);
            }
            if (!input && data.input) setInput(data.input);
            if (data.pendingContextPrefix && !pendingContextPrefix) {
                setPendingContextPrefix(data.pendingContextPrefix);
            }
            // Streaming msg = dấu hiệu agent đang chạy lúc user refresh
            // Phase 1.5: thay vì chỉ "khôi phục snapshot frozen" + fetch history,
            // GIỜ check `/active-turn` → nếu turn vẫn chạy → mở SSE resume → tiếp tục live stream.
            if (data.streamingMsg && !streamingMsg) {
                setStreamingMsg({ ...data.streamingMsg, _stale: true });
                (async () => {
                    try {
                        const at = await api.get(`/agent-hub/conversations/${conversationId}/active-turn`);
                        if (at.data?.active && at.data?.turn_id) {
                            // Còn turn đang chạy → resume SSE → events streaming sẽ tiếp tục
                            setLoading(true);
                            setStreamingMsg({ role: 'assistant', agent: at.data.agent, content: '', tool_trace: [], _resumed: true });
                            activeTurnRef.current = { turn_id: at.data.turn_id, lastEventId: '0-0' };
                            const cbs = buildStreamCallbacks();
                            resumeTurnStream({
                                turn_id: at.data.turn_id,
                                from_id: '0-0',
                                ...cbs,
                            }).catch((e) => {
                                setError(e?.message || 'Resume failed');
                                setStreamingMsg(null);
                            }).finally(() => {
                                setLoading(false);
                            });
                            return;  // KHÔNG fetch history fallback nữa
                        }
                    } catch (e) {
                        console.warn('active-turn check failed:', e);
                    }
                    // Fallback (turn không còn active): fetch history để lấy reply nếu đã done
                    try {
                        const res = await api.get('/agent-hub/history', {
                            params: { conversation_id: conversationId, limit: 3 }
                        });
                        const rows = res.data || [];
                        const latest = rows.length > 0 ? rows[rows.length - 1] : null;
                        if (latest && latest.reply && new Date(latest.created_at).getTime() > data.ts) {
                            setMessages((m) => [...m, {
                                role: 'assistant',
                                agent: latest.agent_name,
                                content: latest.reply,
                                usage: {
                                    cost_usd: latest.cost_usd,
                                    duration_ms: latest.duration_ms,
                                    num_turns: latest.num_turns
                                },
                                historical: true,
                                _recoveredAfterRefresh: true
                            }]);
                        }
                    } catch (e) {
                        console.warn('Cannot fetch history for stale recovery', e);
                    } finally {
                        setStreamingMsg(null);
                    }
                })();
                // skip cũ — không re-run setTimeout fallback
                return;
            }
        } catch (e) {
            console.warn('Cannot restore state', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId]);

    // Cleanup localStorage entries cũ (>7 ngày) — chạy 1 lần khi mount component
    useEffect(() => {
        try {
            const now = Date.now();
            const WEEK = 7 * 24 * 3600 * 1000;
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(STATE_KEY_PREFIX)) {
                    try {
                        const d = JSON.parse(localStorage.getItem(k));
                        if (d?.ts && now - d.ts > WEEK) localStorage.removeItem(k);
                    } catch {
                        localStorage.removeItem(k);
                    }
                }
            }
        } catch {}
    }, []);

    // Phase 1.5: callbacks chung cho cả streamChat (POST) và resumeTurnStream (GET resume)
    const buildStreamCallbacks = useCallback(() => ({
        onTurnOpen: (d) => {
            // Phase 5 (2026-05-24): clear stale pendingQuestion khi turn rotate.
            // Tránh case: turn cũ ask question + chết → user click trên card cũ →
            // submit với turn_id của card cũ. Nếu turn mới re-ask, card mới override
            // pendingQuestion với turn_id mới — đúng route.
            const prevTurn = activeTurnRef.current?.turn_id;
            if (prevTurn && prevTurn !== d.turn_id) {
                setPendingQuestion((cur) => {
                    if (cur && cur.turn_id === prevTurn) {
                        return null; // clear stale question bound to old turn
                    }
                    return cur;
                });
            }
            activeTurnRef.current = { turn_id: d.turn_id, lastEventId: '0-0' };
        },
        onEventId: (eid) => {
            if (activeTurnRef.current) activeTurnRef.current.lastEventId = eid;
        },
        onUserQuestion: (d) => {
            setPendingQuestion(d);
            setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
        },
        onUserQuestionTimeout: (d) => {
            // Agent timeout chờ — clear card nếu vẫn còn
            setPendingQuestion((cur) => cur && cur.question_id === d.question_id ? null : cur);
        },
        onRateLimit: (d) => setRateLimit({ limit: d.limit, remaining: d.remaining }),
        onStart: (d) => { if (d?.rate_limit) setRateLimit(d.rate_limit); },
        onText: (delta) => setStreamingMsg((cur) => ({ ...(cur || { role: 'assistant', agent: selectedAgent?.name, tool_trace: [] }), content: (cur?.content || '') + delta })),
        onToolUse: (d) => setStreamingMsg((cur) => ({
            ...(cur || { role: 'assistant', agent: selectedAgent?.name, content: '', tool_trace: [] }),
            tool_trace: [...(cur?.tool_trace || []), { id: d.id, tool: d.tool, input: d.input, output_preview: '(pending)', success: false }]
        })),
        onToolResult: (d) => setStreamingMsg((cur) => {
            const trace = (cur?.tool_trace || []).map((t) => t.id === d.id ? { ...t, output_preview: d.output_preview, success: d.success } : t);
            return { ...(cur || {}), tool_trace: trace };
        }),
        onDone: (d) => {
            setStreamingMsg((cur) => {
                const final = { ...(cur || { role: 'assistant', agent: selectedAgent?.name }), usage: d?.usage, tool_trace: cur?.tool_trace || [] };
                if (final.content) setMessages((m) => [...m, final]);
                return null;
            });
            activeTurnRef.current = null;
        },
        onServerRestart: (d) => {
            // Sprint 10 (2026-05-22): producer chạy trong Celery worker tách process
            // → API restart KHÔNG ảnh hưởng turn. Event này CHỈ fire trong edge case
            // hiếm (Celery worker cũng crash) → vẫn show card "Gửi lại" cho user.
            setServerRestart({
                message: d?.message || 'Worker bị gián đoạn',
                partial_reply: d?.partial_reply || null,
                last_user_text: lastUserMessageRef.current?.text || '',
                last_pastes: lastUserMessageRef.current?.pastes || [],
                last_images: lastUserMessageRef.current?.images || [],
            });
        },
        onError: (d) => {
            setError(d?.detail || 'Stream error');
            setStreamingMsg(null);
            activeTurnRef.current = null;
        },
    }), [selectedAgent]);

    // Callbacks cho /btw parallel sub-turn (state riêng để không đè main turn)
    const buildBtwStreamCallbacks = useCallback(() => ({
        onTurnOpen: (d) => { btwActiveTurnRef.current = { turn_id: d.turn_id, lastEventId: '0-0' }; },
        onEventId: (eid) => { if (btwActiveTurnRef.current) btwActiveTurnRef.current.lastEventId = eid; },
        onUserQuestion: (d) => {
            setPendingQuestion(d);
            setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
        },
        onUserQuestionTimeout: (d) => setPendingQuestion((cur) => cur && cur.question_id === d.question_id ? null : cur),
        onRateLimit: (d) => setRateLimit({ limit: d.limit, remaining: d.remaining }),
        onText: (delta) => setBtwStreamingMsg((cur) => ({
            ...(cur || { role: 'assistant', agent: selectedAgent?.name, tool_trace: [], isBtw: true }),
            content: (cur?.content || '') + delta
        })),
        onToolUse: (d) => setBtwStreamingMsg((cur) => ({
            ...(cur || { role: 'assistant', agent: selectedAgent?.name, content: '', tool_trace: [], isBtw: true }),
            tool_trace: [...(cur?.tool_trace || []), { id: d.id, tool: d.tool, input: d.input, output_preview: '(pending)', success: false }]
        })),
        onToolResult: (d) => setBtwStreamingMsg((cur) => {
            const trace = (cur?.tool_trace || []).map((t) => t.id === d.id ? { ...t, output_preview: d.output_preview, success: d.success } : t);
            return { ...(cur || {}), tool_trace: trace };
        }),
        onDone: (d) => {
            setBtwStreamingMsg((cur) => {
                const final = { ...(cur || { role: 'assistant', agent: selectedAgent?.name, isBtw: true }), usage: d?.usage, tool_trace: cur?.tool_trace || [] };
                if (final.content) setMessages((m) => [...m, final]);
                return null;
            });
            btwActiveTurnRef.current = null;
        },
        onError: (d) => {
            setError(d?.detail || 'BTW stream error');
            setBtwStreamingMsg(null);
            btwActiveTurnRef.current = null;
        },
    }), [selectedAgent]);

    const sendBtwMessage = async (note, btwPastes = [], btwImages = []) => {
        if (!selectedAgent || !conversationId) return;
        const hasPastes = btwPastes.length > 0;
        const hasImages = btwImages.length > 0;

        let fullMessage = `[BTW PARALLEL — sub-turn chạy song song với turn chính của user. KHÔNG cancel turn cũ, trả lời ngắn gọn cho note này thôi]\n${note}`;
        if (hasPastes) {
            const parts = [fullMessage];
            btwPastes.forEach((p, i) => parts.push(`\n\n[Pasted text #${i + 1} — ${p.lines} lines, ${p.chars} chars]\n${p.content}\n[End paste #${i + 1}]`));
            fullMessage = parts.join('');
        }
        if (hasImages) {
            const imgLines = btwImages.map((img, i) => `[Attached image #${i + 1}: ${img.path} — ${img.mime}, ${img.size} bytes. Dùng Read tool đọc file path]`).join('\n');
            fullMessage = `${fullMessage}\n\n${imgLines}`;
        }

        // Render user bubble với BTW tag
        setMessages((m) => [...m, {
            role: 'user',
            content: note,
            isBtw: true,
            pastes: hasPastes ? btwPastes.map(({ content, ...meta }) => ({ ...meta, content })) : undefined,
            images: hasImages ? btwImages.map(({ previewUrl, ...meta }) => ({ ...meta, previewUrl })) : undefined,
        }]);

        setBtwLoading(true);
        setBtwStreamingMsg({ role: 'assistant', agent: selectedAgent.name, content: '', tool_trace: [], isBtw: true });

        if (btwStreamAbortRef.current) { try { btwStreamAbortRef.current.abort(); } catch {} }
        btwStreamAbortRef.current = new AbortController();

        try {
            await streamChat({
                agent_name: selectedAgent.name,
                message: fullMessage,
                conversation_id: conversationId,
                signal: btwStreamAbortRef.current.signal,
                ...buildBtwStreamCallbacks(),
            });
        } catch (e) {
            if (e?.name !== 'AbortError') {
                setError(e?.message || 'BTW chat failed');
                setBtwStreamingMsg(null);
                btwActiveTurnRef.current = null;
            }
        } finally {
            setBtwLoading(false);
        }
    };

    const restoreConversation = async (cid) => {
        try {
            setLoading(true);
            const res = await api.get('/agent-hub/history', { params: { conversation_id: cid, limit: 200 } });
            const rows = res.data || [];
            if (rows.length === 0) {
                setError('Conversation trống');
                return;
            }
            // Match agent
            const agentName = rows[0].agent_name;
            const agent = agents.find((a) => a.name === agentName);
            if (agent) setSelectedAgent(agent);
            // Build messages
            const msgs = [];
            for (const r of rows) {
                msgs.push({ role: 'user', content: r.message });
                msgs.push({
                    role: 'assistant',
                    agent: r.agent_name,
                    content: r.reply || '(no reply)',
                    tool_trace: [],  // tool_calls stored as JSON but not loaded into trace shape for simplicity
                    usage: { cost_usd: r.cost_usd, duration_ms: r.duration_ms, num_turns: r.num_turns },
                    historical: true
                });
            }
            setMessages(msgs);
            setConversationId(cid);
            setDrawerOpen(false);

            // Phase 1.5: kiểm tra xem có turn đang chạy (vd: refresh giữa chừng) → resume SSE
            try {
                const at = await api.get(`/agent-hub/conversations/${cid}/active-turn`);
                if (at.data?.active && at.data?.turn_id) {
                    // B5: dedupe — nếu tab khác cùng user đã resume turn này → skip
                    if (resumedTurnsRef.current.has(at.data.turn_id)) return;
                    resumedTurnsRef.current.add(at.data.turn_id);
                    setLoading(true);
                    setStreamingMsg({ role: 'assistant', agent: at.data.agent || agentName, content: '', tool_trace: [], _resumed: true });
                    activeTurnRef.current = { turn_id: at.data.turn_id, lastEventId: '0-0' };
                    const cbs = buildStreamCallbacks();
                    if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch {} }
                    streamAbortRef.current = new AbortController();
                    resumeTurnStream({
                        turn_id: at.data.turn_id,
                        from_id: '0-0',
                        signal: streamAbortRef.current.signal,
                        ...cbs,
                    }).catch((e) => {
                        if (e?.name !== 'AbortError') {
                            setError(e?.message || 'Resume stream failed');
                            setStreamingMsg(null);
                        }
                    }).finally(() => {
                        setLoading(false);
                    });
                }
            } catch (e) {
                // active-turn check failure không fatal — chỉ là không resume được
                console.warn('active-turn check failed:', e);
            }
        } catch (e) {
            setError(e?.message || 'Cannot restore conversation');
        } finally {
            setLoading(false);
        }
    };

    // Auto-load conversation history khi tab mount với initialConversationId (lần đầu duy nhất)
    const mountRestoredRef = useRef(false);
    useEffect(() => {
        if (mountRestoredRef.current) return;
        if (initialConversationId && agents && agents.length > 0) {
            mountRestoredRef.current = true;
            restoreConversation(initialConversationId);
        } else if (!initialConversationId && agents && agents.length > 0) {
            mountRestoredRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agents]);

    const sendMessage = async (text, overrideBundle = null) => {
        // overrideBundle = { text, pastes, images } khi flush từ pendingQueue
        const useText = overrideBundle?.text ?? text;
        const usePastes = overrideBundle?.pastes ?? pastes;
        const useImages = overrideBundle?.images ?? images;
        const hasPastes = usePastes.length > 0;
        const hasImages = useImages.length > 0;

        // ── Slash commands parse (chỉ khi không phải override = user submit trực tiếp) ──
        if (!overrideBundle) {
            const trimmed = useText.trim();
            if (trimmed.startsWith('/')) {
                const handled = await handleSlashCommand(trimmed, { hasPastes, hasImages, usePastes, useImages });
                if (handled) return;
                // Nếu không match command → fall through (treat as normal text)
            }
        }

        if ((!useText.trim() && !hasPastes && !hasImages) || !selectedAgent) return;

        // Khi loading: KHÔNG auto-queue nữa. User phải gõ /btw explicit để queue.
        // Submit message thường lúc loading → reject với hint.
        if (loading && !overrideBundle) {
            setError('Agent đang chạy. Gõ /btw <message> để xếp hàng note, hoặc /stop để dừng turn.');
            return;
        }
        // Generate conversation_id nếu chưa có
        let cid = conversationId;
        if (!cid) {
            cid = newConvId();
            setConversationId(cid);
        }

        // Build message: short display + full content gửi backend
        const displayText = useText;
        let fullMessage = useText;
        if (hasPastes) {
            const parts = [useText];
            usePastes.forEach((p, i) => {
                parts.push(
                    `\n\n[Pasted text #${i + 1} — ${p.lines} lines, ${p.chars} chars]\n${p.content}\n[End paste #${i + 1}]`
                );
            });
            fullMessage = parts.join('');
        }
        // Prepend image attachment refs (agent dùng Read tool đọc)
        if (hasImages) {
            const imgLines = useImages.map((img, i) =>
                `[Attached image #${i + 1}: ${img.path} — ${img.mime}, ${img.size} bytes. Dùng Read tool đọc file path này để xem ảnh]`
            ).join('\n');
            fullMessage = `${fullMessage}\n\n${imgLines}`;
        }
        // Prepend carry-over context (sau compact, 1-shot)
        if (pendingContextPrefix) {
            fullMessage =
                `[CONTEXT FROM PREVIOUS COMPACTED CONVERSATION — bạn TIẾP TỤC phiên này, đừng coi là khởi đầu mới]\n` +
                `${pendingContextPrefix}\n` +
                `[END CONTEXT]\n\n` +
                `[Câu hỏi mới của user]\n${fullMessage}`;
            setPendingContextPrefix(null);
        }

        setMessages((m) => [...m, {
            role: 'user',
            content: displayText,
            pastes: hasPastes ? usePastes.map(({ content, ...meta }) => ({ ...meta, content })) : undefined,
            images: hasImages ? useImages.map(({ previewUrl, ...meta }) => ({ ...meta, previewUrl })) : undefined
        }]);
        // Lưu last user message cho nút "Gửi lại" sau server restart
        lastUserMessageRef.current = { text: displayText, pastes: usePastes, images: useImages };
        setServerRestart(null);
        if (!overrideBundle) {
            // Chỉ clear inputs khi user submit trực tiếp (override = flush từ queue, đã clear sẵn lúc enqueue)
            setInput('');
            setPastes([]);
            setImages([]);
        }
        setLoading(true);
        setStreamingMsg({ role: 'assistant', agent: selectedAgent.name, content: '', tool_trace: [] });

        // B4: tạo AbortController để abort stream khi tab unmount
        if (streamAbortRef.current) {
            try { streamAbortRef.current.abort(); } catch {}
        }
        streamAbortRef.current = new AbortController();

        try {
            await streamChat({
                agent_name: selectedAgent.name,
                message: fullMessage,
                conversation_id: cid,
                signal: streamAbortRef.current.signal,
                ...buildStreamCallbacks(),
            });
            // Stream kết thúc bình thường (got `done` event → onDone đã clear activeTurnRef).
            // Nếu activeTurnRef vẫn còn → stream cut giữa chừng (vd API restart) → auto-resume.
            if (activeTurnRef.current?.turn_id) {
                await _autoResumeOnDisconnect(cid, 'Stream cut mid-turn');
            }
        } catch (e) {
            if (e?.name === 'AbortError') {
                // ignore — user navigate hoặc unmount
            } else if (activeTurnRef.current?.turn_id) {
                // Network error giữa chừng turn — thử resume
                await _autoResumeOnDisconnect(cid, e?.message || 'Connection lost');
            } else {
                setError(e?.message || 'Chat failed');
                setStreamingMsg(null);
            }
        } finally {
            setLoading(false);
            if (isActive) inputRef.current?.focus(); // chỉ focus nếu tab đang active
        }
    };

    // Auto-resume khi stream cut giữa chừng (vd network blip). Exponential backoff
    // 2s → 4s → 8s → 16s → 30s. Max 5 retries.
    //
    // QUAN TRỌNG: KHÔNG retry nếu server đã emit `server_restart` event — khi đó
    // session không thể resume (Claude SDK subprocess đã chết). User phải bấm "Gửi lại".
    const _autoResumeOnDisconnect = async (cid, reasonText) => {
        const turnId = activeTurnRef.current?.turn_id;
        if (!turnId) return;
        // Nếu server đã thông báo restart → skip retry, để UI card "Gửi lại" handle
        if (serverRestart) {
            setStreamingMsg(null);
            activeTurnRef.current = null;
            return;
        }
        const delays = [2000, 4000, 8000, 16000, 30000];
        // Show user notice
        setStreamingMsg((cur) => ({
            ...(cur || { role: 'assistant', agent: selectedAgent?.name, content: '', tool_trace: [] }),
            _reconnecting: true,
            _reconnect_reason: reasonText,
        }));

        for (let attempt = 0; attempt < delays.length; attempt++) {
            await new Promise((r) => setTimeout(r, delays[attempt]));
            // Probe active-turn — confirm backend up + turn still alive
            let active = false;
            try {
                const r = await api.get(`/agent-hub/conversations/${cid}/active-turn`);
                active = !!(r.data?.active && r.data?.turn_id === turnId);
            } catch (e) {
                // Backend chưa up — tiếp tục retry
                continue;
            }
            if (!active) {
                // Turn không còn active (đã done hoặc backend cleared). Fetch history fallback.
                try {
                    const res = await api.get('/agent-hub/history', { params: { conversation_id: cid, limit: 2 } });
                    const rows = res.data || [];
                    const latest = rows[rows.length - 1];
                    if (latest?.reply) {
                        setMessages((m) => [...m, {
                            role: 'assistant', agent: latest.agent_name, content: latest.reply,
                            usage: { cost_usd: latest.cost_usd, duration_ms: latest.duration_ms, num_turns: latest.num_turns },
                            historical: true, _recoveredAfterReconnect: true,
                        }]);
                    }
                } catch {}
                setStreamingMsg(null);
                activeTurnRef.current = null;
                return;
            }
            // Backend up + turn active → resume từ lastEventId
            setStreamingMsg((cur) => ({ ...(cur || {}), _reconnecting: false, _resumed: true }));
            try {
                if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch {} }
                streamAbortRef.current = new AbortController();
                await resumeTurnStream({
                    turn_id: turnId,
                    from_id: activeTurnRef.current?.lastEventId || '0-0',
                    signal: streamAbortRef.current.signal,
                    ...buildStreamCallbacks(),
                });
                // Got done event → onDone cleared activeTurnRef
                if (!activeTurnRef.current?.turn_id) return;
                // Re-cut giữa resume → continue loop (try next backoff)
            } catch (e) {
                if (e?.name === 'AbortError') return;
                // Continue retry
            }
        }
        // Max retries exhausted
        setError('Không thể reconnect sau 5 lần thử. Refresh trang để tải lại conversation.');
        setStreamingMsg(null);
        activeTurnRef.current = null;
    };

    // B4: unmount cleanup — abort active streams (main + btw)
    useEffect(() => {
        return () => {
            if (streamAbortRef.current) { try { streamAbortRef.current.abort(); } catch {} }
            if (btwStreamAbortRef.current) { try { btwStreamAbortRef.current.abort(); } catch {} }
        };
    }, []);

    const selectAgent = (a) => {
        setSelectedAgent(a);
        // Đổi agent → reset conversation (1 conversation = 1 agent)
        newConversation();
        setDrawerOpen(false);
    };

    const orchestrator = useMemo(() => agents.find((a) => a.name === ORCHESTRATOR), [agents]);
    const maintAgents = useMemo(() => agents.filter((a) => a.tier === 'maintenance' && a.name !== ORCHESTRATOR), [agents]);
    const devAgents = useMemo(() => agents.filter((a) => a.tier === 'development'), [agents]);
    const isOrchestrator = selectedAgent?.name === ORCHESTRATOR;

    const renderAssistantBody = (m) => (
        <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 1, bgcolor: m.isBtw ? 'secondary.main' : 'primary.main', color: 'white' }}><IconRobot size={16} /></Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{m.agent}</Typography>
                {m.isBtw && <Chip label="/btw" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />}
                {m.historical && <Chip label="lịch sử" size="small" sx={{ height: 18, fontSize: 10 }} />}
            </Box>
            <Box sx={{ pl: 4.5, minWidth: 0, '& > *:first-of-type': { mt: 0 } }}>
                <Md>{m.content}</Md>
                {m.tool_trace?.length > 0 && <ToolTrace trace={m.tool_trace} />}
                {m.usage && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1.5, color: 'text.secondary', fontSize: 11.5 }}>
                        {m.usage.cost_usd > 0 && <span>${m.usage.cost_usd.toFixed(4)}</span>}
                        {m.usage.num_turns > 0 && <span>· {m.usage.num_turns} turn{m.usage.num_turns > 1 ? 's' : ''}</span>}
                        {m.usage.duration_ms > 0 && <span>· {(m.usage.duration_ms / 1000).toFixed(1)}s</span>}
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff', flex: 1, minHeight: 0 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'grey.200', minHeight: 56, flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={() => { setDrawerOpen(true); if (drawerTab === 1) loadConversations(); }}>
                    <IconLayoutSidebar size={20} />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconRobot size={18} color="#6366f1" />
                    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{selectedAgent?.name || 'Agent Hub'}</Typography>
                    {isOrchestrator && <Chip label="master" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />}
                    {selectedAgent && !isOrchestrator && (
                        <Chip label={selectedAgent.tier} size="small" color={selectedAgent.tier === 'maintenance' ? 'warning' : 'info'} sx={{ height: 18, fontSize: 10 }} />
                    )}
                    {conversationId && (
                        <Tooltip title={`Conversation ID: ${conversationId.slice(0, 8)}…`}>
                            <Chip
                                icon={<IconMessage size={12} />}
                                label={`${messages.filter((m) => m.role === 'user').length} msg`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: 11 }}
                            />
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ flex: 1 }} />
                {rateLimit && (
                    <Tooltip title={`Reset trong ${Math.round(rateLimit.reset_in_seconds || 0)}s`}>
                        <Chip
                            label={`${rateLimit.remaining ?? '?'}/${rateLimit.limit} chat`}
                            size="small"
                            color={rateLimit.remaining > 10 ? 'default' : 'warning'}
                            sx={{ height: 22, fontSize: 11 }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Hội thoại mới (tab hiện tại)">
                    <IconButton size="small" onClick={newConversation}>
                        <IconPlus size={18} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Mở conversation hiện tại trong tab BROWSER mới (popout)">
                    <IconButton
                        size="small"
                        onClick={() => window.open(`/workspace/agent-hub${conversationId ? '?conv=' + conversationId : ''}`, '_blank', 'noopener')}
                    >
                        <IconExternalLink size={18} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Drawer */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 340, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: 16 }}>Agent Hub</Typography>
                            <Typography variant="caption" color="text.secondary">{agents.length} agents · Claude Max</Typography>
                        </Box>
                        <Tooltip title="Hội thoại mới">
                            <IconButton size="small" color="primary" onClick={() => { newConversation(); setDrawerOpen(false); }}>
                                <IconPlus size={18} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Tabs value={drawerTab} onChange={(_, v) => { setDrawerTab(v); if (v === 1) loadConversations(); }} variant="fullWidth">
                        <Tab icon={<IconUsersGroup size={16} />} iconPosition="start" label="Agents" sx={{ minHeight: 40, fontSize: 12 }} />
                        <Tab icon={<IconHistory size={16} />} iconPosition="start" label={
                            <Badge color="primary" badgeContent={conversations.length} max={99}>
                                <Box sx={{ pr: 1.5 }}>Hội thoại</Box>
                            </Badge>
                        } sx={{ minHeight: 40, fontSize: 12 }} />
                    </Tabs>
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {drawerTab === 0 && (
                            <>
                                {orchestrator && (
                                    <>
                                        <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 600 }}>🎯 Master</ListSubheader>
                                        <List dense disablePadding>
                                            <ListItemButton selected={selectedAgent?.name === orchestrator.name} onClick={() => selectAgent(orchestrator)}>
                                                <ListItemText primary={<Typography variant="body2" fontWeight={600}>{orchestrator.name}</Typography>} secondary={<Typography variant="caption" color="text.secondary">Orchestrator</Typography>} />
                                            </ListItemButton>
                                        </List>
                                        <Divider />
                                    </>
                                )}
                                <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 600 }}>🔧 Maintenance ({maintAgents.length})</ListSubheader>
                                <List dense disablePadding>
                                    {maintAgents.map((a) => (
                                        <ListItemButton key={a.name} selected={selectedAgent?.name === a.name} onClick={() => selectAgent(a)}>
                                            <ListItemText primary={<Typography variant="body2">{a.name}</Typography>} secondary={<Typography variant="caption" color="text.secondary" noWrap>{a.description}</Typography>} />
                                        </ListItemButton>
                                    ))}
                                </List>
                                <Divider />
                                <ListSubheader sx={{ bgcolor: 'transparent', fontWeight: 600 }}>💻 Development ({devAgents.length})</ListSubheader>
                                <List dense disablePadding>
                                    {devAgents.map((a) => (
                                        <ListItemButton key={a.name} selected={selectedAgent?.name === a.name} onClick={() => selectAgent(a)}>
                                            <ListItemText primary={<Typography variant="body2">{a.name}</Typography>} secondary={<Typography variant="caption" color="text.secondary" noWrap>{a.description}</Typography>} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </>
                        )}
                        {drawerTab === 1 && (
                            <List dense disablePadding>
                                {conversations.length === 0 && (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <IconMessage size={32} style={{ opacity: 0.3 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            Chưa có hội thoại nào.
                                        </Typography>
                                    </Box>
                                )}
                                {conversations.map((c) => (
                                    <Box key={c.conversation_id} sx={{ position: 'relative', borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                        <ListItemButton
                                            selected={c.conversation_id === conversationId}
                                            onClick={() => restoreConversation(c.conversation_id)}
                                            sx={{ alignItems: 'flex-start', py: 1.25, pr: 5 }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" sx={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.4, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {c.first_message}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                                                        <Chip label={c.agent_name} size="small" sx={{ height: 18, fontSize: 10 }}
                                                              color={c.agent_name === ORCHESTRATOR ? 'primary' : (c.agent_name.startsWith('datahub-') ? 'warning' : 'info')}
                                                              variant="outlined" />
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                            {c.message_count} msg · {relTime(c.last_message_at)}
                                                        </Typography>
                                                        {c.total_cost_usd > 0 && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                                                · ${c.total_cost_usd.toFixed(3)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                        <Tooltip title="Mở conversation này trong TAB MỚI (in-app, chạy song song)">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onOpenNewTab?.(c.conversation_id);
                                                    setDrawerOpen(false);
                                                }}
                                                sx={{ position: 'absolute', right: 6, top: 6, p: 0.5, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'grey.100' } }}
                                            >
                                                <IconExternalLink size={14} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
            </Drawer>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, maxWidth: MAX_WIDTH, mx: 'auto', width: '90%' }}>{error}</Alert>
            )}

            {/* Chat */}
            <Box ref={scrollRef} onScroll={handleScrollShrink} sx={{ flex: 1, overflow: 'auto', px: 2, py: 3, minWidth: 0 }}>
                {messages.length === 0 && !streamingMsg && selectedAgent && (
                    <Welcome isOrchestrator={isOrchestrator} agentName={selectedAgent.name} onSample={(s) => sendMessage(s)} />
                )}
                <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', minWidth: 0 }}>
                    {/* Lazy loading sentinel — IntersectionObserver trigger auto-load */}
                    {messages.length > visibleCount && (
                        <Box ref={lazyLoadSentinelRef} sx={{ textAlign: 'center', py: 2 }}>
                            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                <CircularProgress size={14} thickness={4} sx={{ color: 'text.disabled' }} />
                                <Typography variant="caption" color="text.secondary">
                                    Scroll lên để xem {messages.length - visibleCount} tin nhắn cũ hơn
                                </Typography>
                                <Tooltip title="Tóm tắt toàn bộ conversation cũ → 1 summary (giảm context cost)">
                                    <Chip
                                        size="small" label={compacting ? 'Compacting...' : '/compact'}
                                        onClick={!compacting ? handleCompact : undefined}
                                        sx={{ height: 20, fontSize: 10, cursor: compacting ? 'wait' : 'pointer' }}
                                    />
                                </Tooltip>
                            </Stack>
                        </Box>
                    )}
                    {messages.slice(-visibleCount).map((m, i) => {
                        if (m.role === 'user') {
                            return (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, minWidth: 0 }}>
                                    <Box sx={{ maxWidth: '85%', minWidth: 0, bgcolor: m.isBtw ? 'rgba(156,39,176,0.06)' : 'grey.100', borderRadius: 3, px: 2, py: 1.25, border: m.isBtw ? '1px dashed' : 'none', borderColor: 'secondary.main', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                        {m.isBtw && (
                                            <Chip label="/btw" size="small" color="secondary" sx={{ height: 16, fontSize: 9.5, mb: 0.5 }} />
                                        )}
                                        {m.content && (
                                            <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.6, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{m.content}</Typography>
                                        )}
                                        {m.pastes?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: m.content ? 0.75 : 0 }}>
                                                {m.pastes.map((p, pi) => (
                                                    <Chip
                                                        key={pi}
                                                        icon={<IconClipboard size={12} />}
                                                        label={`Pasted #${pi + 1} · +${p.lines} lines`}
                                                        onClick={() => setPreviewPaste(p)}
                                                        size="small"
                                                        sx={{ fontSize: 11, cursor: 'pointer', bgcolor: 'white' }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                        {m.images?.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: m.content || m.pastes?.length ? 0.75 : 0 }}>
                                                {m.images.map((img, ii) => (
                                                    <Box
                                                        key={ii}
                                                        component="img"
                                                        src={img.previewUrl}
                                                        alt={img.filename}
                                                        sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }
                        return <Box key={i} sx={{ mb: 4 }}>{renderAssistantBody(m)}</Box>;
                    })}

                    {streamingMsg && (
                        <Box sx={{ mb: 4 }}>
                            {streamingMsg._stale && (
                                <Alert severity="info" icon={<CircularProgress size={14} />} sx={{ mb: 1.5, py: 0.5 }}>
                                    <Typography variant="caption">
                                        Đang khôi phục tiến trình từ phiên trước (refresh)... Sẽ kiểm tra backend trong vài giây.
                                    </Typography>
                                </Alert>
                            )}
                            {streamingMsg._resumed && (
                                <Alert severity="success" icon={<CircularProgress size={14} />} sx={{ mb: 1.5, py: 0.5 }}>
                                    <Typography variant="caption">
                                        🔄 Đã reconnect vào turn đang chạy của agent — tiếp tục streaming bình thường.
                                    </Typography>
                                </Alert>
                            )}
                            {streamingMsg._reconnecting && (
                                <Alert severity="warning" icon={<CircularProgress size={14} color="warning" />} sx={{ mb: 1.5, py: 0.5 }}>
                                    <Typography variant="caption">
                                        ⏳ Connection lost ({streamingMsg._reconnect_reason || 'unknown'}) — đang thử reconnect... (max 5 lần, ~60s)
                                    </Typography>
                                </Alert>
                            )}
                            {renderAssistantBody(streamingMsg)}
                            {!streamingMsg._stale && (
                                <Box sx={{ pl: 4.5, mt: 0.5 }}>
                                    <Box sx={{ display: 'inline-block', width: 8, height: 14, bgcolor: 'primary.main', animation: 'blink 1s infinite' }} />
                                    <style>{`@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }`}</style>
                                </Box>
                            )}
                        </Box>
                    )}

                    {loading && !streamingMsg && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, pl: 4.5 }}>
                            <CircularProgress size={14} thickness={5} />
                            <Typography variant="caption" color="text.secondary">{isOrchestrator ? 'Master đang khởi tạo…' : 'Đang khởi tạo…'}</Typography>
                        </Box>
                    )}

                    {/* Server restart card — emit khi backend SIGTERM giữa turn */}
                    {serverRestart && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'warning.dark' }}>
                                🔄 Server vừa restart giữa chừng
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {serverRestart.message}
                            </Typography>
                            {serverRestart.partial_reply && (
                                <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1, mb: 1.5, maxHeight: 200, overflow: 'auto' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        Phản hồi 1 phần đã nhận trước restart:
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                                        {serverRestart.partial_reply}
                                    </Typography>
                                </Box>
                            )}
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained" color="warning" size="small"
                                    onClick={() => {
                                        const last = serverRestart;
                                        setServerRestart(null);
                                        sendMessageRef.current?.(last.last_user_text, {
                                            text: last.last_user_text,
                                            pastes: last.last_pastes,
                                            images: last.last_images,
                                        });
                                    }}
                                    disabled={!serverRestart.last_user_text || loading}
                                >
                                    Gửi lại tin nhắn cuối
                                </Button>
                                <Button
                                    variant="outlined" color="inherit" size="small"
                                    onClick={() => setServerRestart(null)}
                                >
                                    Đóng
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {btwStreamingMsg && (
                        <Box sx={{ mb: 4, mt: 2, pl: 1, borderLeft: '3px solid', borderColor: 'secondary.main', pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Chip label="/btw parallel" size="small" color="secondary" sx={{ height: 18, fontSize: 10 }} />
                                <Typography variant="caption" color="text.secondary">sub-turn chạy song song</Typography>
                            </Box>
                            {renderAssistantBody(btwStreamingMsg)}
                            <Box sx={{ pl: 4.5, mt: 0.5 }}>
                                <Box sx={{ display: 'inline-block', width: 8, height: 14, bgcolor: 'secondary.main', animation: 'blink 1s infinite' }} />
                            </Box>
                        </Box>
                    )}
                    {btwLoading && !btwStreamingMsg && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, pl: 4.5 }}>
                            <CircularProgress size={14} thickness={5} color="secondary" />
                            <Typography variant="caption" color="text.secondary">/btw sub-turn đang khởi tạo…</Typography>
                        </Box>
                    )}

                    {/* Phase 2 watcher events — chronological inline cards */}
                    {watcherEvents.length > 0 && (
                        <Box sx={{ my: 2 }}>
                            {watcherEvents.slice(-20).map((ev, i) => (
                                <WatcherCard key={`${ev.watcher_id}-${ev._received_at}-${i}`} event={ev} />
                            ))}
                        </Box>
                    )}

                    {/* Interactive question card — agent chờ user trả lời */}
                    {pendingQuestion && (
                        <UserQuestionCard
                            question={pendingQuestion}
                            onSubmit={async (answer, notes) => {
                                // Phase 5 (2026-05-24): ưu tiên turn_id từ event data
                                // (pin vào turn ASK question), KHÔNG dùng activeTurnRef
                                // (có thể đã rotate sang turn mới). Backend smart-route
                                // lookup actual turn nếu mismatch.
                                const turnId = pendingQuestion.turn_id || activeTurnRef.current?.turn_id;
                                if (!turnId) {
                                    setError('No active turn');
                                    return;
                                }
                                try {
                                    await api.post(`/agent-hub/turns/${turnId}/answer`, {
                                        question_id: pendingQuestion.question_id,
                                        answer,
                                        notes: notes || null,
                                        cancelled: false,
                                    });
                                    setPendingQuestion(null);
                                } catch (e) {
                                    setError(e?.message || 'Submit answer failed');
                                }
                            }}
                            onCancel={async () => {
                                const turnId = pendingQuestion.turn_id || activeTurnRef.current?.turn_id;
                                if (!turnId) { setPendingQuestion(null); return; }
                                try {
                                    await api.post(`/agent-hub/turns/${turnId}/answer`, {
                                        question_id: pendingQuestion.question_id,
                                        cancelled: true,
                                    });
                                } catch (e) {
                                    console.warn('Cancel answer failed', e);
                                }
                                setPendingQuestion(null);
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Pending queue compact bar — collapsible. Đặt NGAY trên input để khỏi chiếm chat list. */}
            {pendingQueue.length > 0 && (
                <Box sx={{ bgcolor: 'info.lighter', borderTop: '1px solid', borderColor: 'info.light' }}>
                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconHourglass size={14} color="#0288d1" />
                        <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 600, fontSize: 12 }}>
                            {pendingQueue.length} <code style={{ background: '#dbeafe', padding: '0 4px', borderRadius: 3, fontFamily: 'monospace', fontSize: 11 }}>/btw</code> note chờ
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'info.dark', opacity: 0.75, fontSize: 11 }}>
                            · tự gửi khi agent xong
                        </Typography>
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Button
                                size="small"
                                onClick={() => setPendingExpanded((v) => !v)}
                                sx={{ fontSize: 11, minWidth: 0, py: 0.25, color: 'info.dark', textTransform: 'none' }}
                            >
                                {pendingExpanded ? '▴ Ẩn' : '▾ Xem chi tiết'}
                            </Button>
                            <Button
                                size="small"
                                onClick={() => { setPendingQueue([]); setPendingExpanded(false); }}
                                sx={{ fontSize: 11, minWidth: 0, py: 0.25 }}
                                color="error"
                            >
                                Huỷ tất cả
                            </Button>
                        </Box>
                    </Box>
                    {pendingExpanded && (
                        <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', px: 2, pb: 1, maxHeight: '30vh', overflow: 'auto' }}>
                            {pendingQueue.map((q, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
                                    <Box sx={{
                                        maxWidth: '85%', position: 'relative',
                                        bgcolor: 'white', borderRadius: 2, px: 1.5, py: 0.75,
                                        border: '1px dashed', borderColor: 'info.light',
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                            <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500, fontSize: 10.5 }}>
                                                #{idx + 1}
                                            </Typography>
                                            {q.pastes?.length > 0 && (
                                                <Chip size="small" icon={<IconClipboard size={10} />} label={q.pastes.length}
                                                      sx={{ height: 16, fontSize: 9.5, '& .MuiChip-icon': { ml: 0.5 } }} />
                                            )}
                                            {q.images?.length > 0 && (
                                                <Chip size="small" icon={<IconPhoto size={10} />} label={q.images.length}
                                                      sx={{ height: 16, fontSize: 9.5, '& .MuiChip-icon': { ml: 0.5 } }} />
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => setPendingQueue((Q) => Q.filter((_, i) => i !== idx))}
                                                sx={{ ml: 'auto', p: 0.25 }}
                                                title="Xoá khỏi queue"
                                            >
                                                <IconX size={11} />
                                            </IconButton>
                                        </Box>
                                        {q.text && (
                                            <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.4, color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {q.text}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Input */}
            {selectedAgent && (
                <Box sx={{ borderTop: '1px solid', borderColor: 'grey.200', bgcolor: '#fff', py: 1 }}>
                    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', px: 2 }}>
                        {/* Image attachment chips — compact 44px horizontal scroll */}
                        {(images.length > 0 || uploadingImage) && (
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.75, overflowX: 'auto', pb: 0.25 }}>
                                {images.map((img) => (
                                    <Box key={img.id} sx={{ position: 'relative', flexShrink: 0 }}>
                                        <Box
                                            component="img"
                                            src={img.previewUrl}
                                            alt={img.filename}
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                objectFit: 'cover',
                                                borderRadius: 0.75,
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                bgcolor: 'grey.50'
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => setImages((arr) => arr.filter((x) => x.id !== img.id))}
                                            sx={{
                                                position: 'absolute', top: -5, right: -5,
                                                width: 16, height: 16, p: 0,
                                                bgcolor: 'background.paper', border: '1px solid', borderColor: 'grey.300',
                                                '&:hover': { bgcolor: 'error.50' }
                                            }}
                                        >
                                            <IconX size={9} />
                                        </IconButton>
                                    </Box>
                                ))}
                                {uploadingImage && (
                                    <Box sx={{ width: 44, height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'grey.300', borderRadius: 0.75 }}>
                                        <CircularProgress size={16} />
                                    </Box>
                                )}
                            </Box>
                        )}
                        {/* Paste attachment chips */}
                        {pastes.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                                {pastes.map((p, i) => (
                                    <Chip
                                        key={p.id}
                                        icon={<IconClipboard size={12} />}
                                        label={`Pasted #${i + 1} · +${p.lines} lines · ${p.chars >= 1000 ? `${(p.chars / 1000).toFixed(1)}K` : p.chars} chars`}
                                        onDelete={() => setPastes((ps) => ps.filter((x) => x.id !== p.id))}
                                        onClick={() => setPreviewPaste(p)}
                                        deleteIcon={<IconX size={14} />}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            fontSize: 11,
                                            cursor: 'pointer',
                                            bgcolor: 'grey.50',
                                            '&:hover': { bgcolor: 'grey.100' }
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                        {/* Slash command autocomplete dropdown — hiện khi input bắt đầu '/' */}
                        {(() => {
                            const trimmed = (input || '').trimStart();
                            const showMenu = trimmed.startsWith('/') && !trimmed.includes(' ');
                            if (!showMenu) return null;
                            const filtered = SLASH_COMMANDS.filter((c) => c.name.startsWith(trimmed));
                            if (filtered.length === 0) return null;
                            return (
                                <Box sx={{
                                    mb: 0.5, p: 0.5,
                                    border: '1px solid', borderColor: 'grey.300',
                                    borderRadius: 1.5, bgcolor: 'white',
                                    boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
                                    maxHeight: 240, overflowY: 'auto',
                                }}>
                                    {filtered.map((c) => (
                                        <Box
                                            key={c.name}
                                            onClick={() => {
                                                // Fill `/cmd ` nếu cmd cần args, else fill `/cmd`
                                                const filled = c.args ? `${c.name} ` : c.name;
                                                setInput(filled);
                                                setTimeout(() => inputRef.current?.focus(), 0);
                                            }}
                                            sx={{
                                                px: 1.25, py: 0.75, borderRadius: 1, cursor: 'pointer',
                                                '&:hover': { bgcolor: 'primary.lighter' },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13, color: 'primary.main' }}>
                                                    {c.name}
                                                </Typography>
                                                {c.args && (
                                                    <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                                                        {c.args}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                                                {c.desc}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })()}
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, border: '1px solid', borderColor: 'grey.300', borderRadius: 2.5, px: 1.5, py: 0.75, '&:focus-within': { borderColor: 'primary.main' } }}>
                            <TextField
                                inputRef={inputRef} fullWidth multiline maxRows={5}
                                placeholder={
                                    loading
                                        ? 'Agent đang chạy. /btw <msg> để gửi note · /stop để dừng · / để xem lệnh…'
                                        : isOrchestrator
                                        ? 'Hỏi master agent… (paste content dài tự động rút gọn · gõ / để xem lệnh)'
                                        : `Chat với ${selectedAgent.name}… (gõ / để xem lệnh)`
                                }
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                                onPaste={async (e) => {
                                    // Detect image trong clipboard trước (vd Screenshot, copy-from-browser)
                                    const items = Array.from(e.clipboardData?.items || []);
                                    const imageItems = items.filter((it) => it.type && it.type.startsWith('image/'));
                                    if (imageItems.length > 0) {
                                        e.preventDefault();
                                        setUploadingImage(true);
                                        try {
                                            for (const it of imageItems) {
                                                const blob = it.getAsFile();
                                                if (!blob) continue;
                                                const fd = new FormData();
                                                const ext = (it.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
                                                fd.append('file', blob, `pasted_${Date.now()}.${ext}`);
                                                const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
                                                const res = await fetch(`${baseUrl}/agent-hub/upload-attachment`, {
                                                    method: 'POST',
                                                    headers: { Authorization: getAuthHeader() },
                                                    body: fd
                                                });
                                                if (!res.ok) throw new Error(`Upload failed HTTP ${res.status}`);
                                                const data = await res.json();
                                                // Create preview URL từ blob để hiển thị chip
                                                const previewUrl = URL.createObjectURL(blob);
                                                setImages((imgs) => [...imgs, {
                                                    id: Date.now() + Math.random(),
                                                    path: data.path,
                                                    filename: data.filename,
                                                    size: data.size_bytes,
                                                    mime: data.mime_type,
                                                    previewUrl
                                                }]);
                                            }
                                        } catch (err) {
                                            setError(err?.message || 'Image upload failed');
                                        } finally {
                                            setUploadingImage(false);
                                        }
                                        return;
                                    }
                                    // Fallback text paste
                                    const text = e.clipboardData.getData('text') || '';
                                    if (!text) return;
                                    const lines = (text.match(/\n/g) || []).length + 1;
                                    if (lines >= PASTE_THRESHOLD_LINES || text.length >= PASTE_THRESHOLD_CHARS) {
                                        e.preventDefault();
                                        setPastes((ps) => [...ps, {
                                            id: Date.now() + Math.random(),
                                            lines,
                                            chars: text.length,
                                            preview: text.slice(0, 120).replace(/\s+/g, ' '),
                                            content: text
                                        }]);
                                    }
                                }}
                                disabled={false}
                                variant="standard"
                                InputProps={{ disableUnderline: true, sx: { fontSize: 15, lineHeight: 1.5 } }}
                            />
                            {loading && activeTurnRef.current?.turn_id ? (
                                <Tooltip title="Dừng agent đang chạy (cancel turn)" placement="top">
                                    <IconButton
                                        onClick={async () => {
                                            const tid = activeTurnRef.current?.turn_id;
                                            if (!tid) return;
                                            try {
                                                await api.post(`/agent-hub/turns/${tid}/stop`);
                                            } catch (e) {
                                                console.warn('stop turn failed', e);
                                            }
                                        }}
                                        sx={{ bgcolor: 'error.main', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'error.dark' } }}
                                    >
                                        <IconPlayerStop size={16} />
                                    </IconButton>
                                </Tooltip>
                            ) : (() => {
                                const isSlash = input.trim().startsWith('/');
                                const canSubmit = (input.trim() || pastes.length > 0 || images.length > 0) && (!loading || isSlash);
                                return (
                                    <Tooltip
                                        title={loading && !isSlash
                                            ? 'Agent đang chạy — chỉ /btw, /stop, /compact, /clear, /help được submit'
                                            : 'Enter gửi · Shift+Enter xuống dòng · gõ / để xem lệnh'}
                                        placement="top"
                                    >
                                        <span>
                                            <IconButton onClick={() => sendMessage(input)} disabled={!canSubmit}
                                                sx={{ bgcolor: canSubmit ? 'primary.main' : 'grey.300', color: 'white', width: 32, height: 32, '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' } }}>
                                                {loading ? <CircularProgress size={14} color="inherit" /> : <IconSend size={16} />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                );
                            })()}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Paste preview dialog */}
            <Dialog open={!!previewPaste} onClose={() => setPreviewPaste(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 15 }}>
                    <IconClipboard size={18} />
                    Pasted content
                    {previewPaste && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                            {previewPaste.lines} lines · {previewPaste.chars} chars
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 1.5,
                            bgcolor: '#fafbfc',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 1,
                            fontSize: 12,
                            fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
                            maxHeight: '60vh',
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {previewPaste?.content || ''}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewPaste(null)}>Đóng</Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (previewPaste?.content) navigator.clipboard.writeText(previewPaste.content);
                        }}
                    >
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ============ Shell: tab bar + multi-instance ChatTab ============
const TABS_KEY = 'agent_hub:tabs';        // list of { tabId, conversationId }
const ACTIVE_TAB_KEY = 'agent_hub:active_tab';

function newTabId() {
    return 't_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
}

function loadTabsFromStorage() {
    try {
        const raw = localStorage.getItem(TABS_KEY);
        if (!raw) return null;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr) || arr.length === 0) return null;
        return arr;
    } catch {
        return null;
    }
}

export default function AgentHubPage() {
    const [agents, setAgents] = useState([]);
    const [agentsError, setAgentsError] = useState(null);

    // URL ?conv=<id> on first load → tab khởi tạo với conv đó
    const initialFromUrl = useMemo(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            return sp.get('conv');
        } catch {
            return null;
        }
    }, []);

    const [tabs, setTabs] = useState(() => {
        const saved = loadTabsFromStorage();
        if (saved && saved.length > 0) {
            // Nếu URL có conv → đảm bảo có tab cho nó, set active
            if (initialFromUrl) {
                const existing = saved.find((t) => t.conversationId === initialFromUrl);
                if (existing) return saved;
                return [...saved, { tabId: newTabId(), conversationId: initialFromUrl, label: null }];
            }
            return saved;
        }
        return [{ tabId: newTabId(), conversationId: initialFromUrl || null, label: null }];
    });

    const [activeTabId, setActiveTabId] = useState(() => {
        const saved = localStorage.getItem(ACTIVE_TAB_KEY);
        if (initialFromUrl) {
            const cur = loadTabsFromStorage() || [];
            const found = cur.find((t) => t.conversationId === initialFromUrl);
            if (found) return found.tabId;
            // Tab mới tạo cho URL conv — lấy ID của tab cuối
            const t = loadTabsFromStorage();
            return t?.[t.length - 1]?.tabId || saved || null;
        }
        return saved || null;
    });

    // Status per tab (streaming, pendingCount) — để render badge ●/⏳
    const [tabStatus, setTabStatus] = useState({}); // { tabId: { streaming, pendingCount } }

    // Persist tabs to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(TABS_KEY, JSON.stringify(tabs.map((t) => ({
                tabId: t.tabId, conversationId: t.conversationId, label: t.label,
            }))));
        } catch {}
    }, [tabs]);

    useEffect(() => {
        if (activeTabId) {
            try { localStorage.setItem(ACTIVE_TAB_KEY, activeTabId); } catch {}
            // Sync URL ?conv với conv của tab active (cho refresh giữ đúng conv)
            const at = tabs.find((t) => t.tabId === activeTabId);
            const sp = new URLSearchParams(window.location.search);
            const cur = sp.get('conv') || '';
            const desired = at?.conversationId || '';
            if (cur !== desired) {
                if (desired) sp.set('conv', desired); else sp.delete('conv');
                window.history.replaceState({}, '', `${window.location.pathname}?${sp.toString()}`);
            }
        }
    }, [activeTabId, tabs]);

    // Fetch agents 1 lần — share cho mọi tab
    useEffect(() => {
        let mounted = true;
        api.get('/agent-hub/agents').then((res) => {
            if (!mounted) return;
            setAgents(res.data || []);
        }).catch((e) => mounted && setAgentsError(e?.message || 'Cannot load agents'));
        return () => { mounted = false; };
    }, []);

    // Đảm bảo activeTabId luôn hợp lệ
    useEffect(() => {
        if (!tabs.find((t) => t.tabId === activeTabId)) {
            setActiveTabId(tabs[0]?.tabId || null);
        }
    }, [tabs, activeTabId]);

    const handleConvChange = useCallback((tabId, convId, label) => {
        setTabs((prev) => prev.map((t) =>
            t.tabId === tabId ? { ...t, conversationId: convId, label: label || t.label } : t
        ));
    }, []);

    const handleStatusChange = useCallback((tabId, status) => {
        setTabStatus((prev) => ({ ...prev, [tabId]: status }));
    }, []);

    const addTab = useCallback((initialConvId = null) => {
        const t = { tabId: newTabId(), conversationId: initialConvId, label: null };
        setTabs((prev) => [...prev, t]);
        setActiveTabId(t.tabId);
    }, []);

    const closeTab = useCallback((tabId) => {
        const status = tabStatus[tabId];
        if (status?.streaming) {
            if (!window.confirm('Tab này đang stream. Đóng sẽ ngắt theo dõi (turn vẫn chạy nền, có thể mở lại từ history). Tiếp tục?')) return;
        }
        setTabs((prev) => {
            const next = prev.filter((t) => t.tabId !== tabId);
            if (next.length === 0) {
                // Auto-tạo 1 tab fresh nếu vừa đóng tab cuối
                return [{ tabId: newTabId(), conversationId: null, label: null }];
            }
            return next;
        });
        setTabStatus((s) => { const { [tabId]: _, ...rest } = s; return rest; });
    }, [tabStatus]);

    if (agentsError) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Cannot load agents: {agentsError}
            </Alert>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)', bgcolor: '#fff' }}>
            {/* Tab bar */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.5,
                borderBottom: '1px solid', borderColor: 'grey.200',
                bgcolor: 'grey.50',
                overflowX: 'auto', flexShrink: 0,
            }}>
                {tabs.map((t) => {
                    const isActive = t.tabId === activeTabId;
                    const st = tabStatus[t.tabId] || {};
                    const label = t.label || (t.conversationId ? `Conv ${t.conversationId.slice(0, 8)}` : 'New chat');
                    return (
                        <Box
                            key={t.tabId}
                            onClick={() => setActiveTabId(t.tabId)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                px: 1.5, py: 0.5,
                                minWidth: 120, maxWidth: 220,
                                borderRadius: '6px 6px 0 0',
                                bgcolor: isActive ? '#fff' : 'transparent',
                                border: '1px solid',
                                borderColor: isActive ? 'grey.300' : 'transparent',
                                borderBottom: isActive ? 'none' : '1px solid transparent',
                                cursor: 'pointer',
                                position: 'relative',
                                mb: isActive ? '-1px' : 0,
                                '&:hover': { bgcolor: isActive ? '#fff' : 'grey.100' },
                            }}
                        >
                            {/* Badge chỉ hiện cho tab KHÔNG active — tránh visual noise khi user đang nhìn stream */}
                            {st.streaming && !isActive && (
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', animation: 'blink 1s infinite' }} title="Đang stream" />
                            )}
                            {!st.streaming && st.pendingCount > 0 && !isActive && (
                                <IconHourglass size={12} color="#f59e0b" />
                            )}
                            <Typography sx={{
                                fontSize: 12.5, fontWeight: isActive ? 600 : 400,
                                color: isActive ? 'text.primary' : 'text.secondary',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                flex: 1,
                            }}>
                                {label}
                            </Typography>
                            {st.pendingCount > 0 && st.streaming && (
                                <Chip size="small" label={st.pendingCount} sx={{ height: 14, fontSize: 9.5, '& .MuiChip-label': { px: 0.5 } }} />
                            )}
                            {tabs.length > 1 && (
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); closeTab(t.tabId); }}
                                    sx={{ p: 0.25, ml: 0.25 }}
                                    title="Đóng tab"
                                >
                                    <IconX size={11} />
                                </IconButton>
                            )}
                        </Box>
                    );
                })}
                <IconButton
                    size="small"
                    onClick={() => addTab(null)}
                    sx={{ ml: 0.5, p: 0.5 }}
                    title="Tab mới (Ctrl+T)"
                >
                    <IconPlus size={16} />
                </IconButton>
                <style>{`@keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0.3; } }`}</style>
            </Box>

            {/* Render TẤT CẢ ChatTab — hidden non-active để giữ state + SSE running */}
            <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
                {tabs.map((t) => (
                    <Box
                        key={t.tabId}
                        sx={{
                            position: 'absolute', inset: 0,
                            display: t.tabId === activeTabId ? 'flex' : 'none',
                            flexDirection: 'column',
                        }}
                    >
                        <ChatTab
                            tabId={t.tabId}
                            initialConversationId={t.conversationId}
                            agents={agents}
                            isActive={t.tabId === activeTabId}
                            onConvChange={handleConvChange}
                            onStatusChange={handleStatusChange}
                            onOpenNewTab={addTab}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
